import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaTrash, FaPlus, FaArrowLeft, FaEdit } from 'react-icons/fa';
import { auth } from '../firebase';
import { API_ENDPOINTS } from '../config/backendConfig';
import BackButton from '../components/BackButton';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';

export default function CharacterLibrary() {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        loadCharacterLibrary(user.uid);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadCharacterLibrary = async (userId) => {
    // Check cache first
    const cacheKey = createCacheKey('characters', { library: userId }, userId);
    const cached = getCache(cacheKey);
    if (cached) {
      console.log('✅ [CHARACTERS] Using cached character library:', cached.length);
      setCharacters(cached);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/library/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        const charactersData = data.characters || [];
        
        // Cache the results (5 min cache - characters don't change often)
        setCache(cacheKey, charactersData, 5 * 60 * 1000);
        console.log('✅ [CHARACTERS] Character library cached');
        
        setCharacters(charactersData);
      } else {
        setError('Failed to load character library');
      }
    } catch (error) {
      console.error('Error loading character library:', error);
      setError('Failed to load character library');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharacter = async (characterId, characterName) => {
    if (!window.confirm(`Are you sure you want to delete "${characterName}" from your character library? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete character');
      }

      // Remove the character from the local state
      setCharacters(prev => prev.filter(char => char.id !== characterId));
      
      // Clear cache to force refresh
      const cacheKey = createCacheKey('characterLibrary', {}, currentUser.uid);
      clearCache(cacheKey);
      console.log('✅ [CHARACTER LIBRARY] Cache cleared after delete');
      
      console.log('Character deleted successfully:', characterName);
      
      // Show success message
      alert(`Character "${characterName}" has been deleted from your library.`);
      
    } catch (error) {
      console.error('Error deleting character:', error);
      alert('Failed to delete character. Please try again.');
    }
  };

  const handleCreateNewCharacter = () => {
    navigate('/storyboarding-studio');
  };

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-amber-800">Loading your character library...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => loadCharacterLibrary(currentUser.uid)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      {loading && hasLoadedOnce && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded-full shadow">
          Refreshing character library...
        </div>
      )}
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <BackButton />
              <h1 className="text-2xl font-bold text-amber-800">Character Library</h1>
            </div>
            <button
              onClick={handleCreateNewCharacter}
              className="bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-300"
            >
              <FaPlus className="text-sm" />
              <span>Create New Character</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {characters.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Your Character Library is Empty</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Start building your character collection by creating your first character. 
              You can use these characters in your stories and scenes.
            </p>
            <button
              onClick={handleCreateNewCharacter}
              className="bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-all duration-300"
            >
              <FaPlus className="text-sm" />
              <span>Create Your First Character</span>
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Your Characters ({characters.length})
              </h2>
              <p className="text-gray-600">
                Manage your character collection. Click on a character to select it, or hover to see delete options.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {characters.map((character) => (
                <div
                  key={character.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group cursor-pointer border border-amber-200 hover:border-amber-400"
                >
                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCharacter(character.id, character.name);
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg transition-all duration-200 z-10 opacity-0 group-hover:opacity-100"
                    title={`Delete ${character.name}`}
                  >
                    <FaTrash className="text-xs" />
                  </button>

                  <div className="p-4">
                    {/* Character Image */}
                    <div className="w-full h-48 bg-amber-100 rounded-lg flex items-center justify-center overflow-hidden border border-amber-200 mb-4">
                      {character.imageUrl ? (
                        <img 
                          src={character.imageUrl} 
                          alt={character.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <FaUser className="text-amber-600 text-4xl" style={{ display: character.imageUrl ? 'none' : 'flex' }} />
                    </div>

                    {/* Character Details */}
                    <div className="text-center">
                      <h3 className="font-bold text-amber-800 text-lg mb-2">{character.name}</h3>
                      <div className="text-sm text-amber-600 mb-2">
                        {character.age && character.gender ? `${character.age} • ${character.gender}` : character.age || character.gender || 'Character'}
                      </div>
                      {character.role && (
                        <div className="text-xs text-amber-500 mb-2">{character.role}</div>
                      )}
                      {character.description && (
                        <p className="text-xs text-gray-600 line-clamp-2">{character.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
} 